import React, { useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

interface URLInputProps {
  onSubmit: (url: string) => void;
  isProcessing: boolean;
}

export const URLInput: React.FC<URLInputProps> = ({ onSubmit, isProcessing }) => {
  const [url, setUrl] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  const validateYouTubeURL = useCallback((url: string): boolean => {
    if (!url.trim()) return false;
    
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrl(value);
    setHasInteracted(true);
    
    if (value.trim()) {
      setIsValid(validateYouTubeURL(value));
    } else {
      setIsValid(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onSubmit(url);
    }
  };

  const handlePaste = () => {
    setTimeout(() => {
      setHasInteracted(true);
    }, 100);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Start Your Summarization
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Paste any YouTube video URL below and watch as we transform it into a concise, 
              actionable summary with full transcript.
            </p>
          </div>

          <div className="relative">
            <div className="relative">
              <input
                type="text"
                value={url}
                onChange={handleInputChange}
                onPaste={handlePaste}
                placeholder="https://www.youtube.com/watch?v=..."
                className={`w-full px-6 py-4 pr-20 text-lg border-2 rounded-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-100 ${
                  hasInteracted && isValid === false
                    ? 'border-red-300 bg-red-50'
                    : hasInteracted && isValid === true
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200 bg-gray-50 focus:border-blue-300 focus:bg-white'
                }`}
                disabled={isProcessing}
              />
              
              {/* Validation Icon */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                {isProcessing ? (
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                ) : hasInteracted && isValid === true ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : hasInteracted && isValid === false ? (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                ) : null}
              </div>
            </div>

            {/* Character Count */}
            <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
              <div>
                {hasInteracted && isValid === false && (
                  <span className="text-red-500 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>Please enter a valid YouTube URL</span>
                  </span>
                )}
                {hasInteracted && isValid === true && (
                  <span className="text-green-500 flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4" />
                    <span>Valid YouTube URL detected</span>
                  </span>
                )}
              </div>
              <span>{url.length}/200</span>
            </div>
          </div>

          {/* Summary Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="radio"
                id="short"
                name="summary-length"
                value="short"
                defaultChecked
                className="peer sr-only"
              />
              <label
                htmlFor="short"
                className="flex flex-col items-center p-4 bg-gray-50 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:bg-gray-100 peer-checked:border-blue-500 peer-checked:bg-blue-50"
              >
                <div className="text-lg font-semibold text-gray-900">Quick</div>
                <div className="text-sm text-gray-600">2-3 minutes</div>
              </label>
            </div>
            <div className="relative">
              <input
                type="radio"
                id="medium"
                name="summary-length"
                value="medium"
                className="peer sr-only"
              />
              <label
                htmlFor="medium"
                className="flex flex-col items-center p-4 bg-gray-50 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:bg-gray-100 peer-checked:border-blue-500 peer-checked:bg-blue-50"
              >
                <div className="text-lg font-semibold text-gray-900">Detailed</div>
                <div className="text-sm text-gray-600">5-7 minutes</div>
              </label>
            </div>
            <div className="relative">
              <input
                type="radio"
                id="long"
                name="summary-length"
                value="long"
                className="peer sr-only"
              />
              <label
                htmlFor="long"
                className="flex flex-col items-center p-4 bg-gray-50 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:bg-gray-100 peer-checked:border-blue-500 peer-checked:bg-blue-50"
              >
                <div className="text-lg font-semibold text-gray-900">Complete</div>
                <div className="text-sm text-gray-600">10+ minutes</div>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid || isProcessing}
            className={`w-full py-4 px-8 text-lg font-semibold rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 ${
              isValid && !isProcessing
                ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing Video...</span>
              </>
            ) : (
              <>
                <span>Generate Summary</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};